import React, { useState, useEffect } from 'react';
import { sendRequest } from '../../api';

const TopicList = ({ topics, onSelectTopic, onCreateNew, onDeleteTopic }) => {
  const [contentCounts, setContentCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Fetch content counts for all topics
  useEffect(() => {
    const fetchContentCounts = async () => {
      if (topics.length === 0) {
        setLoadingCounts(false);
        return;
      }

      try {
        setLoadingCounts(true);
        const counts = {};
        
        // Fetch content counts for all topics in parallel
        const countPromises = topics.map(async (topic) => {
          try {
            const content = await sendRequest(`/topics/${topic.id}/content`, 'GET');
            counts[topic.id] = content ? content.length : 0;
          } catch (err) {
            console.error(`Failed to fetch content count for topic ${topic.id}:`, err);
            counts[topic.id] = 0;
          }
        });

        await Promise.all(countPromises);
        setContentCounts(counts);
      } catch (err) {
        console.error('Error fetching content counts:', err);
      } finally {
        setLoadingCounts(false);
      }
    };

    fetchContentCounts();
  }, [topics]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
      {/* Create new topic card */}
      <button
        onClick={onCreateNew}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-purple-500 dark:hover:border-purple-400 transition-colors group min-h-[300px] flex flex-col"
      >
        <div className="flex flex-col items-center justify-center h-full">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
            <span className="text-white font-bold text-lg">AI</span>
          </div>
          <span className="text-lg font-medium text-gray-700 dark:text-gray-300 group-hover:text-purple-500 dark:group-hover:text-purple-400 text-center">
            What do you want to track?
          </span>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
            Tell me anything you want to monitor on the internet
          </p>
        </div>
      </button>

      {/* Existing topics */}
      {topics.map((topic) => (
        <div key={topic.id} className="relative h-full">
          <button
            onClick={() => onSelectTopic(topic)}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow text-left w-full h-full flex flex-col min-h-[300px]"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex-1 pr-2">
                {topic.name}
              </h3>
              <div className="flex flex-col items-end gap-1">
                <span className={`inline-block px-2 py-1 text-xs rounded ${
                  topic.status === 'active' 
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                }`}>
                  {topic.status || 'active'}
                </span>
                {/* Content count */}
                <div className="flex items-center gap-1">
                  {loadingCounts ? (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>...</span>
                    </div>
                  ) : (
                    <span className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                      (contentCounts[topic.id] || 0) > 0 
                        ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {contentCounts[topic.id] || 0} results
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2 flex-grow">
              {topic.description || 'No description'}
            </p>
            
            <div className="space-y-2 mt-auto">
              {topic.query_terms && topic.query_terms.length > 0 && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Keywords:
                  </span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {topic.query_terms.slice(0, 3).map((term, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded"
                      >
                        {term}
                      </span>
                    ))}
                    {topic.query_terms.length > 3 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        +{topic.query_terms.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show monitored sources count if available */}
              {((topic.sources && topic.sources.length > 0) || (topic.monitored_urls && topic.monitored_urls.length > 0)) && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    Sources: {topic.sources ? topic.sources.length : topic.monitored_urls.length} monitored
                  </span>
                </div>
              )}
              
              <div className="flex items-center justify-end pt-2">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </button>
          <button
            className="absolute top-2 right-2 p-1 text-red-500 hover:text-red-700 bg-white dark:bg-gray-900 rounded-full shadow z-10"
            title="Delete topic"
            onClick={e => { e.stopPropagation(); onDeleteTopic(topic.id); }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ))}
    </div>
  );
};

export default TopicList; 